"use client";

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

export interface ImageSliderProps {
  /** Any JSX elements (images or HTML) to display */
  children: React.ReactNode;
  /** Autoplay delay in milliseconds; set to 0 or undefined to disable autoplay */
  autoplayDelay?: number;
  /** Slide transition speed in milliseconds (default: 500ms) */
  transitionSpeed?: number;
  /** Enable or disable looping of slides (default: true) */
  loop?: boolean;
  /** Enable or disable pagination dots (default: true) */
  pagination?: boolean;
  /** Callback function called when slide changes */
  onSlideChange?: (swiper: SwiperType) => void;
  /** Callback function called when swiper is initialized */
  onSwiper?: (swiper: SwiperType) => void;
}

const ImageSlider: React.FC<ImageSliderProps> = ({
  children,
  autoplayDelay = 3000,
  transitionSpeed = 500,
  loop = true,
  pagination = true,
  onSlideChange,
  onSwiper,
}) => (
  <Swiper
    modules={[Autoplay, Pagination]}
    autoplay={
      autoplayDelay && autoplayDelay > 0
        ? { delay: autoplayDelay, disableOnInteraction: false }
        : false
    }
    speed={transitionSpeed}
    loop={loop}
    pagination={
      pagination
        ? {
            clickable: true,
            bulletClass: 'swiper-pagination-bullet mx-1',
            bulletActiveClass: 'swiper-pagination-bullet-active bg-blue-600',
          }
        : false
    }
    onSlideChange={onSlideChange}
    onSwiper={onSwiper}
    className="w-full h-full"
  >
    {React.Children.map(children, (child, index) => (
      <SwiperSlide key={index} className="flex justify-center items-center">
        {child}
      </SwiperSlide>
    ))}
  </Swiper>
);

export default ImageSlider;
